import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { toast as globalToast } from '@/hooks/use-toast';
import { type CallBackData, csvBoxSingleton } from '@/lib/csv-box';
import {
  type ToastUpdate,
  bindDealItemsInProgressToastToDatasource,
  clearDealItemsInProgressToast,
  registerDealItemsInProgressToast,
} from '@/lib/deal-items-toast';
import {
  DatasourceInstructionMode,
  DealItemAnchorKey,
  PricingMode,
} from '@/lib/graphql/generated/graphql';
import { useEditPricingMode } from '@/lib/hooks/useEditPricingMode';
import { useUploadCsv } from '@/lib/hooks/useUploadCsv';
import { detectEmerchColumns } from '@/lib/mapping';
import { trackEvent } from '@/lib/mixpanel';
import {
  dynamicColumnsForPricingMode,
  flattenDynamicData,
  mergeUnmappedColumns,
  sanitizeFilename,
} from '@/lib/utils/csv-import';
import { getDomainErrorMessage } from '@/lib/utils/domain-error-translator';
import { createCsvFileFromImportData } from '@/lib/utils/import-data-to-csv';
import { pricingModeState } from '@/states/pricing-mode.state';
import { EmerchConsentRequest } from '@/types/emerch-consent';
import {
  type PricingModeChoice,
  PricingModeRequest,
} from '@/types/pricing-mode-request';
import { useAtomValue } from 'jotai';

import { useUser } from '../hooks/use-user';

type CsvBoxImportButtonProps = {
  dealId: string;
  onUploadSuccess?: () => Promise<void>;
  onClick?: () => void;
  onCloseModal?: () => void;
  instructionMode: 'add' | 'update';
  /** Anchor key used to match existing products during import (mostly for update flows) */
  anchorKey?: 'auto' | 'url' | 'sku';
  autoOpen?: boolean;
  hidden?: boolean;
  onEmerchConsentRequested?: (request: EmerchConsentRequest) => void;
  /**
   * Demande le choix du mode de calcul des prix (sales internes uniquement),
   * affiché comme étape du wizard avant l'ouverture de CSVBox. Absent => pas de
   * choix de mode (ex. flux marque, toujours BuyingPriceFixed).
   */
  onPricingModeRequested?: (request: PricingModeRequest) => void;
};

export function CsvBoxImportButton({
  dealId,
  onClick,
  onUploadSuccess,
  onCloseModal,
  instructionMode,
  anchorKey = 'auto',
  autoOpen,
  hidden,
  onEmerchConsentRequested,
  onPricingModeRequested,
}: CsvBoxImportButtonProps) {
  const { t, i18n } = useTranslation();

  const user = useUser();
  const pricingMode = useAtomValue(pricingModeState);
  const { handlePricingModeChange } = useEditPricingMode(dealId);
  const graphqlInstructionMode =
    instructionMode === 'add'
      ? DatasourceInstructionMode.CreateOnly
      : instructionMode === 'update'
        ? DatasourceInstructionMode.UpdateOnly
        : undefined;

  const graphqlAnchorKey =
    anchorKey === 'auto'
      ? null
      : anchorKey === 'url'
        ? DealItemAnchorKey.Url
        : DealItemAnchorKey.Sku;

  const { addCsvDataSourceToDeal } = useUploadCsv(
    dealId,
    graphqlInstructionMode,
    graphqlAnchorKey
  );

  const handleCsvBoxUpload = useCallback(
    async (data: CallBackData) => {
      const filename = sanitizeFilename(data.original_filename);
      let toastTrackerId: string | undefined;

      // Time the rep spent in the CSVBox importer (open → completion callback)
      const importerOpenedAt = importerOpenedAtRef.current;
      importerOpenedAtRef.current = null;
      if (importerOpenedAt !== null) {
        trackEvent('IMPORT_CSV_UPLOADED', {
          dealId,
          instructionMode,
          durationMs: Math.round(performance.now() - importerOpenedAt),
          rowCount: data.row_count,
          rowSuccess: data.row_success,
          rowFail: data.row_fail,
        });
      }

      try {
        if (!data.rows || data.rows.length === 0) {
          throw new Error(
            "Aucune donnée reçue. Activez l'option « Send JSON data to client » dans le Destination du sheet CSVBox."
          );
        }

        // Aplatit les colonnes dynamiques (_dynamic_data) au niveau racine pour
        // qu'elles soient incluses dans le CSV envoyé au backend (signed URL) :
        // le nom de colonne canonique suffit au mapping, pas besoin de les
        // injecter en variable de mutation.
        const importRows = flattenDynamicData(data.rows);

        // Detect emerch columns before upload to ask for consent
        const emerchColumns = detectEmerchColumns(data.raw_columns, importRows);
        let eMerchCustomFieldsHeaders: string[] | undefined;

        if (emerchColumns.length > 0 && onEmerchConsentRequested) {
          const selectedColumns = await new Promise<string[] | null>(
            (resolve) => {
              onEmerchConsentRequested({
                dealId,
                columns: emerchColumns,
                onDecision: resolve,
              });
            }
          );

          if (selectedColumns && selectedColumns.length > 0) {
            eMerchCustomFieldsHeaders = selectedColumns;
          }
        }

        const { update, dismiss } = globalToast({
          description: `⏳ ${t('general.csv_import_in_progress')}`,
          duration: Number.POSITIVE_INFINITY,
        });
        toastTrackerId = registerDealItemsInProgressToast(
          update as ToastUpdate,
          dismiss
        );

        const rows = eMerchCustomFieldsHeaders
          ? mergeUnmappedColumns(importRows, eMerchCustomFieldsHeaders)
          : importRows;

        // Persiste le mode de calcul choisi (modale) juste avant l'upload.
        const pendingPricingMode = pendingPricingModeRef.current;
        pendingPricingModeRef.current = null;
        if (pendingPricingMode && pendingPricingMode !== pricingMode) {
          await handlePricingModeChange(pendingPricingMode);
        }

        const file = createCsvFileFromImportData(rows, filename);
        const result = await addCsvDataSourceToDeal(
          file,
          eMerchCustomFieldsHeaders
        );

        if (!result.success) {
          clearDealItemsInProgressToast({ trackerId: toastTrackerId });
          const errorMessage = getDomainErrorMessage(
            result.domainError.code,
            t,
            'general.error_import_csv_message'
          );
          const errorTitle =
            result.domainError.code !== 'IMPORT_FAILED'
              ? t('general.error_import_csv')
              : t('general.error_import');
          globalToast({
            title: errorTitle,
            description: errorMessage,
            variant: 'destructive',
          });
          return;
        }

        bindDealItemsInProgressToastToDatasource(toastTrackerId, result.id);

        await onUploadSuccess?.();
      } catch (error) {
        if (toastTrackerId) {
          clearDealItemsInProgressToast({ trackerId: toastTrackerId });
        }
        globalToast({
          title: t('general.error_import'),
          description:
            error instanceof Error ? error.message : t('general.error_unknown'),
          variant: 'destructive',
        });
      }
    },
    [
      addCsvDataSourceToDeal,
      dealId,
      handlePricingModeChange,
      instructionMode,
      onEmerchConsentRequested,
      onUploadSuccess,
      pricingMode,
      t,
    ]
  );

  const handleCsvBoxComplete = useCallback(
    (result: boolean, data: CallBackData | null) => {
      // Any import callback (success or failure) means the rep engaged with the
      // import — the upcoming onClose must not be counted as an abandonment.
      importCompletedRef.current = true;
      if (result && data) {
        handleCsvBoxUpload(data);
      }
    },
    [handleCsvBoxUpload]
  );

  // Fired by CSVBox when the modal closes. If no import happened during this
  // session, the rep left the importer → track the abandonment with the time
  // spent, to compare time-to-complete vs time-to-abandon.
  const handleCsvBoxClose = useCallback(() => {
    const importerOpenedAt = importerOpenedAtRef.current;
    importerOpenedAtRef.current = null;
    if (!importCompletedRef.current && importerOpenedAt !== null) {
      trackEvent('IMPORT_CSV_IMPORTER_ABANDONED', {
        dealId,
        instructionMode,
        durationMs: Math.round(performance.now() - importerOpenedAt),
      });
    }
    // Session over: drop the import subscription if it never fired (abandon),
    // since this component may already be unmounted (the modal outlives it).
    pendingUnsubscribeRef.current?.();
    pendingUnsubscribeRef.current = null;
    pendingCloseUnsubscribeRef.current = null;
  }, [dealId, instructionMode]);

  const pendingUnsubscribeRef = useRef<null | (() => void)>(null);
  const pendingCloseUnsubscribeRef = useRef<null | (() => void)>(null);
  // Timestamp (performance.now) of when the CSVBox importer modal was opened,
  // used to measure how long the rep spends preparing the CSV.
  const importerOpenedAtRef = useRef<number | null>(null);
  // Whether an import callback fired during the current modal session (used to
  // distinguish a completed import from an abandonment on close).
  const importCompletedRef = useRef(false);
  // Mode de calcul choisi dans la modale (au clic), à persister juste avant
  // l'upload — pas avant, pour ne rien changer si la sale abandonne.
  const pendingPricingModeRef = useRef<PricingMode | null>(null);

  const openCsvBox = (modeForColumns?: PricingMode) => {
    const language = i18n.language as 'fr' | 'en';
    // Repart d'un importer propre : CSVBox cumule les colonnes dynamiques sur une
    // même instance, donc sans reset un mode précédent « fuiterait » ses colonnes
    // prix sur l'ouverture suivante (ex. rétrocession en mode Marge fixe).
    csvBoxSingleton.reset();
    csvBoxSingleton.setOptions({ language });
    csvBoxSingleton.setUser({
      user_id: user?.providerUser.sub ?? 'unauthenticated',
      instruction_mode: instructionMode,
      anchor_key: anchorKey,
    });
    // Colonnes prix attendues pour CE mode. On (re)définit TOUJOURS, avec [] quand
    // la sale ne met pas les prix à jour (skip), pour ne rien hériter d'avant.
    csvBoxSingleton.setDynamicColumns(
      modeForColumns ? dynamicColumnsForPricingMode(modeForColumns) : []
    );
    // S'abonner une fois juste avant l'ouverture du modal
    pendingUnsubscribeRef.current?.();
    pendingCloseUnsubscribeRef.current?.();
    pendingUnsubscribeRef.current =
      csvBoxSingleton.subscribeOnce(handleCsvBoxComplete);
    pendingCloseUnsubscribeRef.current =
      csvBoxSingleton.subscribeOnceClose(handleCsvBoxClose);
    importCompletedRef.current = false;
    importerOpenedAtRef.current = performance.now();
    trackEvent('IMPORT_CSV_IMPORTER_OPENED', { dealId, instructionMode });
    csvBoxSingleton.openModal();
    onCloseModal?.();
  };

  const handleClick = async () => {
    onClick?.();

    // Flux produits : choix de la base de saisie des prix AVANT d'ouvrir CSVBox.
    // C'est une ÉTAPE du wizard (même Dialog d'import), dont le wording/les
    // options s'adaptent au profil (interne Choose vs marque) — cf.
    // ViewProductsPricingMode. On NE ferme donc PAS la Dialog ici : elle reste
    // ouverte pour afficher l'étape, et sera fermée par openCsvBox (onCloseModal)
    // une fois le choix confirmé et CSVBox ouvert.
    if (onPricingModeRequested) {
      const choice = await new Promise<PricingModeChoice | null>((resolve) => {
        onPricingModeRequested({
          dealId,
          initialMode: pricingMode,
          onDecision: resolve,
        });
      });

      // Annulation → on n'ouvre pas l'import.
      if (choice === null) {
        return;
      }

      // « Je ne mets pas de prix à jour » : ni mutation, ni colonnes prix
      // dynamiques, on ouvre simplement CSVBox.
      if (choice === 'skip') {
        pendingPricingModeRef.current = null;
        openCsvBox();
        return;
      }

      // On NE persiste pas tout de suite : la mutation updateDealPricingMode
      // est faite juste avant l'upload (cf. handleCsvBoxUpload), pour ne pas
      // modifier le deal si la sale abandonne l'import.
      pendingPricingModeRef.current = choice;
      openCsvBox(choice);
      return;
    }

    pendingPricingModeRef.current = null;
    openCsvBox();
  };

  // Auto open when required (only when autoOpen toggles, not when handleClick identity changes)
  useEffect(() => {
    if (autoOpen) {
      handleClick();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- intentional: run only when autoOpen changes
  }, [autoOpen]);

  // NOTE: we intentionally do NOT unsubscribe from the CSVBox singleton on
  // unmount. This component is designed to unmount right after opening the
  // modal (e.g. autoOpen+hidden in view-products-menu calls onCloseModal which
  // removes it from the tree), while the CSVBox modal lives on. Unsubscribing
  // on unmount would drop the completion/close handlers before the user
  // finishes, so IMPORT_CSV_UPLOADED / IMPORT_CSV_IMPORTER_ABANDONED (and the
  // upload itself) would never fire. The handlers use refs + module-level
  // trackEvent (no setState), and subscriptions are cleaned up per session:
  // subscribeOnce self-removes on completion, and handleCsvBoxClose drops the
  // import subscription on close.

  if (hidden) {
    return null;
  }

  return (
    <button
      className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
      onClick={handleClick}
    >
      {t('general.next')}
    </button>
  );
}
